import React, { useEffect, useMemo, useState } from 'react';
import {
  Repeat, Plus, Search, ExternalLink, PauseCircle, PlayCircle, Pencil, XCircle, Trash2,
  CalendarDays,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { adminService } from '../../services/adminService';
import NewRecurringOrderDialog from './NewRecurringOrderDialog';

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  cancelled: 'bg-gray-100 text-gray-600',
  pending: 'bg-amber-100 text-amber-800',
  needs_attention: 'bg-red-100 text-red-800',
  placed: 'bg-green-100 text-green-800',
  skipped: 'bg-gray-100 text-gray-600',
  auto_skipped: 'bg-gray-100 text-gray-600',
  not_deliverable: 'bg-red-100 text-red-700',
};

const statusBadge = (status, testId) => (
  <Badge className={STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'} data-testid={testId}>
    {(status || '').replace(/_/g, ' ')}
  </Badge>
);

const originBadge = (origin, testId) => {
  if (!origin) return null;
  return (
    <Badge
      variant="outline"
      className={origin === 'admin' ? 'border-orange-400 text-orange-700 bg-orange-50' : 'border-blue-400 text-blue-700 bg-blue-50'}
      data-testid={testId}
    >
      {origin === 'admin' ? 'Admin-created' : 'Client-created'}
    </Badge>
  );
};

const prettyDate = (s) => (s
  ? new Date(`${s}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  })
  : '—');

const AdminRecurringOrders = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [filterClient, setFilterClient] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOrigin, setFilterOrigin] = useState('all');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const [confirmAction, setConfirmAction] = useState(null); // {type, template}

  const loadRows = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterClient && filterClient !== 'all') filters.client_account_id = filterClient;
      if (filterStatus && filterStatus !== 'all') filters.status = filterStatus;
      if (filterOrigin && filterOrigin !== 'all') filters.origin = filterOrigin;
      if (search) filters.q = search;
      setRows(await adminService.getRecurringTemplates(filters));
    } catch {
      toast.error('Failed to load recurring schedules');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const cs = await adminService.getClients({ status: 'active' });
      setClients(Array.isArray(cs) ? cs : (cs?.clients || []));
    } catch {
      toast.error('Failed to load clients');
    }
  };

  useEffect(() => { loadClients(); }, []);

  // Deep-link support: /admin/recurring-orders?client=<accountId> pre-selects
  // the client filter (used by the per-client Recurring tab).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const c = params.get('client');
    if (c) setFilterClient(c);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRows(); }, [filterClient, filterStatus, filterOrigin]);

  const onSearch = (e) => {
    if (e.key === 'Enter') loadRows();
  };

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await adminService.getRecurringTemplate(id));
    } catch {
      toast.error('Failed to load schedule details');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (!detail?.id) return;
    try {
      setDetail(await adminService.getRecurringTemplate(detail.id));
    } catch { /* silent */ }
  };

  const openCreate = () => {
    setEditingTemplate(null);
    setDialogOpen(true);
  };

  const openEdit = async (row) => {
    try {
      const full = await adminService.getRecurringTemplate(row.id);
      setEditingTemplate(full);
      setDialogOpen(true);
    } catch {
      toast.error('Failed to load schedule for editing');
    }
  };

  const doStatus = async (row, status) => {
    try {
      await adminService.setRecurringTemplateStatus(row.id, status);
      toast.success(`Schedule ${status === 'cancelled' ? 'cancelled' : status === 'paused' ? 'paused' : 'resumed'}`);
      loadRows();
      if (detail?.id === row.id) refreshDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update schedule');
    }
  };

  const doDelete = async (row, hard) => {
    try {
      await adminService.deleteRecurringTemplate(row.id, hard);
      toast.success(hard ? 'Schedule deleted' : 'Schedule cancelled');
      loadRows();
      setDetailOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete schedule');
    }
  };

  const doOccurrence = async (occ, verb) => {
    try {
      if (verb === 'confirm') {
        const res = await adminService.confirmRecurringOccurrence(occ.id);
        toast.success(`Order ${res.order_number} placed`);
      } else {
        await adminService.skipRecurringOccurrence(occ.id);
        toast.success('Occurrence skipped');
      }
      refreshDetail();
    } catch (e) {
      const detail409 = e.response?.data;
      if (e.response?.status === 409 && detail409?.occurrence) {
        toast.error('This occurrence was already actioned — refreshing view.');
        refreshDetail();
      } else {
        toast.error(detail409?.detail || `Failed to ${verb} occurrence`);
      }
    }
  };

  const filteredRows = rows;  // filtering happens server-side; render as-is
  const clientNamesById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c.company_name])), [clients]);

  return (
    <div className="space-y-6" data-testid="admin-recurring-orders-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2" data-testid="admin-recurring-title">
            <Repeat className="h-6 w-6 text-orange-600" /> Recurring Orders
          </h1>
          <p className="text-sm text-gray-500">
            Create, edit, pause/resume, cancel, and administer recurring schedules for any client.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="admin-recurring-new-btn">
          <Plus className="h-4 w-4 mr-1" /> New schedule
        </Button>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-5 gap-3">
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger data-testid="filter-client">
              <SelectValue placeholder="All clients" />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="all">All clients</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`filter-client-option-${c.id}`}>
                  {c.company_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterOrigin} onValueChange={setFilterOrigin}>
            <SelectTrigger data-testid="filter-origin">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any origin</SelectItem>
              <SelectItem value="client">Client-created</SelectItem>
              <SelectItem value="admin">Admin-created</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onSearch}
              placeholder="Search company, cadence, PO, notes… (press Enter)"
              className="pl-9"
              data-testid="filter-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Rows */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500" data-testid="loading-rows">Loading…</div>
          ) : filteredRows.length === 0 ? (
            <div className="p-8 text-center text-gray-500" data-testid="empty-rows">No schedules match your filters yet.</div>
          ) : (
            <div className="divide-y">
              {filteredRows.map((row) => (
                <div
                  key={row.id}
                  className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                  data-testid={`recurring-row-${row.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900" data-testid={`row-client-${row.id}`}>
                        {row.client_name}
                      </span>
                      {statusBadge(row.status, `row-status-${row.id}`)}
                      {originBadge(row.origin, `row-origin-${row.id}`)}
                    </div>
                    <div className="text-sm text-gray-600 flex flex-wrap gap-x-3">
                      <span data-testid={`row-cadence-${row.id}`}>{row.cadence_label}</span>
                      <span data-testid={`row-items-${row.id}`}>· {row.item_count} item{row.item_count === 1 ? '' : 's'}</span>
                      <span data-testid={`row-location-${row.id}`}>· {row.location_name || 'Unknown location'}</span>
                      {row.next_delivery_date && (
                        <span data-testid={`row-next-${row.id}`}>· next {prettyDate(row.next_delivery_date)}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => openDetail(row.id)}
                      data-testid={`row-view-${row.id}`}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" /> View
                    </Button>
                    {row.status !== 'cancelled' && (
                      <Button
                        variant="outline" size="sm"
                        onClick={() => openEdit(row)}
                        data-testid={`row-edit-${row.id}`}
                      >
                        <Pencil className="h-4 w-4 mr-1" /> Edit
                      </Button>
                    )}
                    {row.status === 'active' && (
                      <Button
                        variant="outline" size="sm"
                        onClick={() => setConfirmAction({ type: 'pause', row })}
                        data-testid={`row-pause-${row.id}`}
                      >
                        <PauseCircle className="h-4 w-4 mr-1" /> Pause
                      </Button>
                    )}
                    {row.status === 'paused' && (
                      <Button
                        variant="outline" size="sm"
                        onClick={() => doStatus(row, 'active')}
                        data-testid={`row-resume-${row.id}`}
                      >
                        <PlayCircle className="h-4 w-4 mr-1" /> Resume
                      </Button>
                    )}
                    {row.status !== 'cancelled' && (
                      <Button
                        variant="outline" size="sm"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => setConfirmAction({ type: 'cancel', row })}
                        data-testid={`row-cancel-${row.id}`}
                      >
                        <XCircle className="h-4 w-4 mr-1" /> Cancel
                      </Button>
                    )}
                    {row.can_hard_delete && (
                      <Button
                        variant="outline" size="sm"
                        className="text-red-700 border-red-400 hover:bg-red-50"
                        onClick={() => setConfirmAction({ type: 'hard_delete', row })}
                        data-testid={`row-hard-delete-${row.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> Delete
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail panel */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="recurring-detail-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Repeat className="h-5 w-5 text-orange-600" />
              Recurring schedule
            </SheetTitle>
            <SheetDescription>Manage occurrences and template settings.</SheetDescription>
          </SheetHeader>

          {detailLoading || !detail ? (
            <div className="p-8 text-center text-gray-500" data-testid="detail-loading">Loading…</div>
          ) : (
            <div className="space-y-6 pt-4">
              <div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {statusBadge(detail.status, 'detail-status')}
                  {originBadge(detail.origin, 'detail-origin')}
                </div>
                <div className="text-lg font-medium" data-testid="detail-client">{detail.client_name}</div>
                <div className="text-sm text-gray-600" data-testid="detail-cadence">{detail.cadence_label} · {detail.item_count} items</div>
                <div className="text-sm text-gray-600">{detail.location_name || '—'}</div>
                {detail.next_delivery_date && (
                  <div className="text-sm text-gray-600" data-testid="detail-next">
                    Next: {prettyDate(detail.next_delivery_date)}
                  </div>
                )}
                {detail.po_number && (
                  <div className="text-sm text-gray-600">PO: {detail.po_number}</div>
                )}
                {detail.notes && (
                  <div className="text-sm text-gray-600">Notes: {detail.notes}</div>
                )}
              </div>

              {/* Items */}
              <div>
                <div className="text-sm font-medium mb-2">Items</div>
                <div className="rounded-md border divide-y">
                  {(detail.items || []).map((i) => (
                    <div key={i.product_id} className="p-2 flex justify-between text-sm" data-testid={`detail-item-${i.product_id}`}>
                      <span>{i.product_name}</span>
                      <span className="text-gray-600">× {i.quantity}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Occurrences */}
              <div>
                <div className="text-sm font-medium mb-2 flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" /> Occurrences
                </div>
                {(detail.occurrences || []).length === 0 ? (
                  <div className="text-sm text-gray-500">No occurrences yet.</div>
                ) : (
                  <div className="rounded-md border divide-y">
                    {(detail.occurrences || []).map((o) => (
                      <div key={o.id} className="p-3 space-y-1" data-testid={`detail-occ-${o.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="text-sm font-medium">
                            {prettyDate(o.delivery_date)}
                          </div>
                          {statusBadge(o.status, `detail-occ-status-${o.id}`)}
                        </div>
                        {o.deadline_label && (
                          <div className="text-xs text-gray-500">{o.deadline_label}</div>
                        )}
                        {o.order_number && (
                          <div className="text-xs text-gray-600" data-testid={`detail-occ-order-${o.id}`}>
                            Placed as order <span className="font-medium">{o.order_number}</span>
                            {o.order_status && ` · ${o.order_status.replace(/_/g, ' ')}`}
                          </div>
                        )}
                        {['pending', 'needs_attention'].includes(o.status) && (
                          <div className="flex gap-2 pt-1">
                            <Button
                              size="sm" variant="outline"
                              onClick={() => doOccurrence(o, 'confirm')}
                              data-testid={`detail-occ-confirm-${o.id}`}
                            >
                              Place now
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => doOccurrence(o, 'skip')}
                              data-testid={`detail-occ-skip-${o.id}`}
                            >
                              Skip
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Create/Edit dialog */}
      <NewRecurringOrderDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={() => { loadRows(); refreshDetail(); }}
        clients={clients}
        editing={editingTemplate}
      />

      {/* Confirmations */}
      <AlertDialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === 'pause' && 'Pause this recurring schedule?'}
              {confirmAction?.type === 'cancel' && 'Cancel this recurring schedule?'}
              {confirmAction?.type === 'hard_delete' && 'Delete this recurring schedule?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === 'pause' && 'Future unplaced dates will be skipped until the schedule is resumed. Already-placed orders are not affected.'}
              {confirmAction?.type === 'cancel' && 'Future unplaced dates will be skipped. Already-placed orders are not affected.'}
              {confirmAction?.type === 'hard_delete' && 'This schedule has never placed an order, so it can be removed entirely. This cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Never mind</AlertDialogCancel>
            <AlertDialogAction
              className={confirmAction?.type === 'hard_delete' ? 'bg-red-600 hover:bg-red-700' : ''}
              onClick={() => {
                if (!confirmAction) return;
                if (confirmAction.type === 'pause') doStatus(confirmAction.row, 'paused');
                if (confirmAction.type === 'cancel') doStatus(confirmAction.row, 'cancelled');
                if (confirmAction.type === 'hard_delete') doDelete(confirmAction.row, true);
                setConfirmAction(null);
              }}
              data-testid="confirm-confirm"
            >
              {confirmAction?.type === 'pause' && 'Pause'}
              {confirmAction?.type === 'cancel' && 'Cancel schedule'}
              {confirmAction?.type === 'hard_delete' && 'Delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminRecurringOrders;
